import React, { useState, memo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Upload, Send } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/Button';
export function CareersPage() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    position: 'Waiter / Server',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      console.log('Application submitted:', formState);
      setIsSubmitting(false);
      setIsSuccess(true);
      setFormState({
        name: '',
        email: '',
        phone: '',
        position: 'Waiter / Server',
        message: ''
      });
    }, 1500);
  };
  const inputClasses = 'w-full bg-navy-900 border-2 border-navy-700 text-white px-4 py-3 focus:border-ocean-500 focus:outline-none focus:ring-1 focus:ring-ocean-500 transition-all duration-300 placeholder-gray-500 rounded-none';
  const labelClasses = 'block text-gold-500 font-heading font-bold text-sm uppercase tracking-wide mb-2';
  const responsibilities = ['Welcome and serve guests professionally', 'Take accurate food and drink orders', 'Deliver excellent customer service', 'Maintain cleanliness of service areas', 'Work closely with kitchen and bar staff', 'Handle payments and guest requests'];
  const requirements = ['Previous experience preferred but not essential', 'Friendly and positive attitude', 'Strong communication skills', 'Ability to work evenings and weekends', 'Team-oriented mindset'];
  const benefits = ['Competitive pay', 'Flexible working hours', 'Supportive team environment', 'Growth opportunities', 'Staff meals and discounts'];
  return <div className="min-h-screen bg-navy-900 text-white selection:bg-gold-500 selection:text-navy-900 flex flex-col" data-id="element-186">
      <Navigation data-id="element-187" />

      <main className="flex-grow pt-24 pb-16" data-id="element-188">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-189">
          <div className="text-center mb-16" data-id="element-190">
            <h1 className="font-heading font-bold text-4xl md:text-5xl text-white mb-4" data-id="element-191">
              Join Our Team
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-192">
              We're looking for passionate individuals to help us create
              memorable dining experiences at The Dock Wall.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start" data-id="element-193">
            {/* Job Description */}
            <div className="space-y-10" data-id="element-194">
              <div data-id="element-195">
                <div className="inline-block bg-ocean-500/10 text-ocean-500 px-4 py-1 rounded-full text-sm font-bold uppercase tracking-wider mb-4 border border-ocean-500/20" data-id="element-196">
                  Now Hiring
                </div>
                <h2 className="font-heading font-bold text-3xl text-white mb-4" data-id="element-197">
                  Waiter / Server
                </h2>
                <p className="text-gray-300 leading-relaxed" data-id="element-198">
                  The Dock Wall is looking for a friendly, energetic, and
                  customer-focused Waiter / Server to join our team. We are
                  seeking someone passionate about hospitality and providing
                  excellent customer service.
                </p>
              </div>

              <div data-id="element-199">
                <h3 className="font-heading font-bold text-xl text-gold-500 mb-4" data-id="element-200">
                  Responsibilities
                </h3>
                <ul className="space-y-3" data-id="element-201">
                  {responsibilities.map((item, index) => <li key={index} className="flex items-start gap-3" data-id="element-202">
                      <CheckCircle className="w-5 h-5 text-ocean-500 flex-shrink-0 mt-0.5" data-id="element-203" />
                      <span className="text-gray-300" data-id="element-204">{item}</span>
                    </li>)}
                </ul>
              </div>

              <div data-id="element-205">
                <h3 className="font-heading font-bold text-xl text-gold-500 mb-4" data-id="element-206">
                  Requirements
                </h3>
                <ul className="space-y-3" data-id="element-207">
                  {requirements.map((item, index) => <li key={index} className="flex items-start gap-3" data-id="element-208">
                      <CheckCircle className="w-5 h-5 text-ocean-500 flex-shrink-0 mt-0.5" data-id="element-209" />
                      <span className="text-gray-300" data-id="element-210">{item}</span>
                    </li>)}
                </ul>
              </div>

              <div data-id="element-211">
                <h3 className="font-heading font-bold text-xl text-gold-500 mb-4" data-id="element-212">
                  What We Offer
                </h3>
                <ul className="space-y-3" data-id="element-213">
                  {benefits.map((item, index) => <li key={index} className="flex items-start gap-3" data-id="element-214">
                      <CheckCircle className="w-5 h-5 text-ocean-500 flex-shrink-0 mt-0.5" data-id="element-215" />
                      <span className="text-gray-300" data-id="element-216">{item}</span>
                    </li>)}
                </ul>
              </div>
            </div>

            {/* Application Form */}
            <div className="lg:sticky lg:top-28" data-id="element-217">
              <div className="bg-navy-800 p-8 border border-navy-700 shadow-xl relative overflow-hidden" data-id="element-218">
                <div className="absolute top-0 right-0 w-32 h-32 bg-gold-500/5 rounded-full blur-3xl -mr-16 -mt-16" data-id="element-219" />

                <h3 className="font-heading font-bold text-2xl text-white mb-6 relative z-10" data-id="element-220">
                  Apply Now
                </h3>

                {isSuccess ? <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="text-center py-12" data-id="element-221">
                    <div className="w-16 h-16 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-222">
                      <Send className="w-8 h-8" data-id="element-223" />
                    </div>
                    <h3 className="text-xl font-heading font-bold text-white mb-4" data-id="element-224">
                      Application Sent
                    </h3>
                    <p className="text-gray-300 mb-8" data-id="element-225">
                      Thank you for your interest in joining The Dock Wall team.
                      We will review your application and get back to you
                      shortly.
                    </p>
                    <Button variant="primary" onClick={() => setIsSuccess(false)} data-id="element-226">
                      Submit Another Application
                    </Button>
                  </motion.div> : <form onSubmit={handleSubmit} className="space-y-5 relative z-10" data-id="element-227">
                    <div data-id="element-228">
                      <label htmlFor="name" className={labelClasses} data-id="element-229">
                        Full Name
                      </label>
                      <input type="text" id="name" name="name" required className={inputClasses} value={formState.name} onChange={handleChange} data-id="element-230" />
                    </div>

                    <div data-id="element-231">
                      <label htmlFor="email" className={labelClasses} data-id="element-232">
                        Email Address
                      </label>
                      <input type="email" id="email" name="email" required className={inputClasses} value={formState.email} onChange={handleChange} data-id="element-233" />
                    </div>

                    <div data-id="element-234">
                      <label htmlFor="phone" className={labelClasses} data-id="element-235">
                        Phone Number
                      </label>
                      <input type="tel" id="phone" name="phone" required className={inputClasses} value={formState.phone} onChange={handleChange} data-id="element-236" />
                    </div>

                    <div data-id="element-237">
                      <label htmlFor="position" className={labelClasses} data-id="element-238">
                        Position Applied For
                      </label>
                      <input type="text" id="position" name="position" readOnly className={`${inputClasses} bg-navy-900/50 text-gray-400 cursor-not-allowed`} value={formState.position} data-id="element-239" />
                    </div>

                    <div data-id="element-240">
                      <label className={labelClasses} data-id="element-241">Upload CV</label>
                      <div className="border-2 border-dashed border-navy-600 rounded-none p-6 text-center hover:border-ocean-500 transition-colors cursor-pointer bg-navy-900/50" data-id="element-242">
                        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" data-id="element-243" />
                        <p className="text-sm text-gray-400" data-id="element-244">
                          Click to upload or drag and drop
                        </p>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-245">
                          PDF, DOC, DOCX up to 5MB
                        </p>
                      </div>
                    </div>

                    <div data-id="element-246">
                      <label htmlFor="message" className={labelClasses} data-id="element-247">
                        Message / Availability
                      </label>
                      <textarea id="message" name="message" rows={4} className={inputClasses} placeholder="Tell us a bit about yourself and when you can start..." value={formState.message} onChange={handleChange} data-id="element-248" />
                    </div>

                    <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isSubmitting} data-id="element-249">
                      {isSubmitting ? 'Submitting...' : 'Submit Application'}
                    </Button>
                  </form>}
              </div>

              <div className="mt-6 text-center" data-id="element-250">
                <p className="text-gray-400 text-sm" data-id="element-251">
                  Prefer to email us directly? <br data-id="element-252" />
                  <a href="mailto:thedockwall@gmail.com" className="text-ocean-500 hover:text-ocean-400 font-semibold" data-id="element-253">
                    thedockwall@gmail.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </main>

      <Footer data-id="element-254" />
    </div>;
}